<script setup lang="ts">
import { logger } from '@/utils/logger';
import { useGaiaToast } from '@/composables/useGaiaToast';
import { computed, ref, watch } from 'vue';
import type { SelectOption } from 'naive-ui';
import { getLlmUsageStatistics, type UsageStatistics } from '@/api';
import StatisticsStackedBarChartCard from '@/components/admin/StatisticsStackedBarChartCard.vue';
import StatisticsSunburstCard from '@/components/charts/StatisticsSunburstCard.vue';
import StatisticsKpiGrid, {
  type StatisticsKpiTile,
} from '@/components/admin/StatisticsKpiGrid.vue';
import { buildStatisticsDateQuery, type StatisticsFiltersProps } from '@/utils/adminStatistics';
import GaiaSelect from '@/components/design/base/selects/GaiaSelect.vue';
import GaiaSegmented from '@/components/design/base/GaiaSegmented.vue';
import {
  buildSunburst,
  buildTimeSeries,
  toNumber,
  type Dimension,
  type Metric,
  type SunburstPath,
} from '@/utils/llmUsageStatistics';

const props = defineProps<StatisticsFiltersProps>();
const message = useGaiaToast();

const data = ref<UsageStatistics | undefined>(undefined);
const loading = ref(false);

const metric = ref<Metric>('cost');
const sunburstPathKey = ref<string>('org-user-token');
const timeDimension = ref<Dimension>('org');

const euroFormatter = new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function formatEuro(value: number): string {
  return euroFormatter.format(value);
}

function formatCount(value: number): string {
  return value.toLocaleString();
}

const metricOptions: { label: string; value: Metric }[] = [
  { label: 'Cost (€)', value: 'cost' },
  { label: 'Tokens', value: 'tokens' },
  { label: 'Calls', value: 'calls' },
];

const METRIC_LABELS: Record<Metric, string> = {
  cost: 'Cost (€)',
  tokens: 'Tokens',
  calls: 'Calls',
};

const sunburstPaths: Record<string, { label: string; path: SunburstPath }> = {
  'org-user-token': {
    label: 'Org → User → Token type',
    path: { dimensions: ['org', 'user'], tokenTypeLeaf: true },
  },
  'platform-org-user': {
    label: 'Platform → Org → User',
    path: { dimensions: ['platform', 'org', 'user'] },
  },
  'org-purpose-model': {
    label: 'Org → Purpose → Model',
    path: { dimensions: ['org', 'purpose', 'model'] },
  },
};

const sunburstPathOptions = computed<SelectOption[]>(() =>
  Object.entries(sunburstPaths).map(([value, { label }]) => ({ label, value }))
);

const dimensionOptions: { label: string; value: Dimension }[] = [
  { label: 'Platform', value: 'platform' },
  { label: 'Organisation', value: 'org' },
  { label: 'User', value: 'user' },
  { label: 'Provider', value: 'provider' },
  { label: 'Model', value: 'model' },
  { label: 'Purpose', value: 'purpose' },
];

const rows = computed(() => data.value?.rows ?? []);

const metricLabel = computed(() => METRIC_LABELS[metric.value]);
const formatMetric = computed(() => (metric.value === 'cost' ? formatEuro : formatCount));

const sunburstData = computed(() =>
  buildSunburst(rows.value, sunburstPaths[sunburstPathKey.value].path, metric.value)
);

const timeSeries = computed(() => buildTimeSeries(rows.value, timeDimension.value, metric.value));

const totalCost = computed<number>(() => {
  if (!data.value) return 0;
  return (
    toNumber(data.value.total_input_cost) +
    toNumber(data.value.total_output_cost) +
    toNumber(data.value.total_cached_input_cost)
  );
});

const kpiTiles = computed<StatisticsKpiTile[]>(() => {
  if (data.value === undefined) {
    return [];
  }

  return [
    { label: 'Total cost', value: formatEuro(totalCost.value) },
    { label: 'Input cost', value: formatEuro(toNumber(data.value.total_input_cost)) },
    { label: 'Output cost', value: formatEuro(toNumber(data.value.total_output_cost)) },
    {
      label: 'Cached input cost',
      value: formatEuro(toNumber(data.value.total_cached_input_cost)),
    },
    { label: 'Total calls', value: data.value.total_call_count.toLocaleString() },
  ];
});

async function fetchStatistics(): Promise<void> {
  loading.value = true;
  try {
    const response = await getLlmUsageStatistics({
      query: {
        included_org_uids: props.includedOrgUids,
        excluded_org_uids: props.excludedOrgUids,
        ...buildStatisticsDateQuery(props.dateRange),
      },
    });
    if (response.response?.status === 200) {
      data.value = response.data;
    }
  } catch (error) {
    message.error('Failed to load model cost statistics');
    logger.error('Failed to load model cost statistics:', error);
  } finally {
    loading.value = false;
  }
}

watch(props, fetchStatistics, { deep: true, immediate: true });
</script>

<template>
  <n-spin :show="loading">
    <n-flex v-if="data !== undefined" vertical :size="16">
      <statistics-kpi-grid :tiles="kpiTiles" />

      <gaia-segmented v-model:value="metric" :options="metricOptions" />

      <n-flex vertical :size="8">
        <n-flex justify="end">
          <gaia-select
            v-model:value="sunburstPathKey"
            :options="sunburstPathOptions"
            size="small"
            style="width: 240px"
          />
        </n-flex>
        <statistics-sunburst-card
          title="Usage breakdown"
          :data="sunburstData"
          :format-value="formatMetric"
          empty-description="No usage in the selected range"
        />
      </n-flex>

      <n-flex vertical :size="8">
        <n-flex justify="end">
          <gaia-select
            v-model:value="timeDimension"
            :options="dimensionOptions"
            size="small"
            style="width: 200px"
          />
        </n-flex>
        <statistics-stacked-bar-chart-card
          :title="`${metricLabel} over time`"
          :categories="timeSeries.days"
          :series="timeSeries.series"
          x-label="Day"
          :y-label="metricLabel"
          :format-value="formatMetric"
          empty-description="No usage in the selected range"
        />
      </n-flex>
    </n-flex>
    <div v-else style="min-height: 200px" />
  </n-spin>
</template>

<style scoped></style>
